import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";

import img1 from "../assets/gallery/gallery_1.jpeg";
import img2 from "../assets/gallery/gallery_2.jpeg";
import img3 from "../assets/gallery/gallery_3.jpeg";
import img4 from "../assets/gallery/gallery_4.jpeg";
import img5 from "../assets/gallery/gallery_5.jpeg";
import img6 from "../assets/gallery/gallery_6.jpeg";
import img7 from "../assets/gallery/gallery_7.jpeg";
import img8 from "../assets/gallery/gallery_8.jpg";
import img9 from "../assets/gallery/gallery_9.jpg";
import img10 from "../assets/gallery/gallery_10.jpg";
import img11 from "../assets/gallery/gallery_11.jpg";
import img12 from "../assets/gallery/gallery_12.jpg";
import img13 from "../assets/gallery/gallery_13.JPEG";
import img14 from "../assets/gallery/gallery_14.JPEG";
import img15 from "../assets/gallery/gallery_15.JPEG";
import img16 from "../assets/gallery/gallery_16.JPEG";

import { Divider } from "./Ornament";
import { easeManor } from "../constants/constants";
import { listGalleryImages } from "../lib/gallery";
import type { GalleryImage } from "../lib/gallery";
import { isSupabaseConfigured } from "../lib/supabase";
import { trackEvent } from "../lib/analytics";

type Piece = { id: string; src: string; span: "sm" | "md" | "lg" | "tall"; tilt: number };

const bundledFallback: Piece[] = [
  { id: "g1", src: img1, span: "lg", tilt: -0.8 },
  { id: "g2", src: img2, span: "sm", tilt: 0.6 },
  { id: "g3", src: img3, span: "md", tilt: -0.4 },
  { id: "g4", src: img4, span: "tall", tilt: 0.9 },
  { id: "g5", src: img5, span: "md", tilt: -1.1 },
  { id: "g6", src: img6, span: "sm", tilt: 0.5 },
  { id: "g7", src: img7, span: "lg", tilt: -0.5 },
  { id: "g8", src: img8, span: "md", tilt: 0.8 },
  { id: "g9", src: img9, span: "tall", tilt: -0.9 },
  { id: "g10", src: img10, span: "sm", tilt: 0.4 },
  { id: "g11", src: img11, span: "md", tilt: -0.7 },
  { id: "g12", src: img12, span: "lg", tilt: 0.6 },
  { id: "g13", src: img13, span: "sm", tilt: -0.3 },
  { id: "g14", src: img14, span: "tall", tilt: 0.8 },
  { id: "g15", src: img15, span: "md", tilt: -1.0 },
  { id: "g16", src: img16, span: "sm", tilt: 0.7 },
];

const SPANS: Piece["span"][] = ["lg", "sm", "md", "tall", "md", "sm", "lg", "md", "tall", "sm", "md", "lg"];
const TILTS = [-0.8, 0.6, -0.4, 0.9, -1.1, 0.5, -0.5, 0.8, -0.9, 0.4, -0.7, 0.6, -0.3, 0.8, -1.0, 0.7];

const toPieces = (imgs: GalleryImage[]): Piece[] =>
  imgs.map((img, i) => ({
    id: img.id,
    src: img.url,
    span: SPANS[i % SPANS.length],
    tilt: TILTS[i % TILTS.length],
  }));

const heightFor = (span: Piece["span"]) =>
  span === "lg" ? "h-80" : span === "tall" ? "h-[30rem]" : span === "md" ? "h-64" : "h-52";

const Gallery: React.FC = () => {
  const [pieces, setPieces] = useState<Piece[]>(bundledFallback);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    let cancelled = false;
    listGalleryImages()
      .then((imgs) => {
        if (cancelled) return;
        if (imgs.length > 0) setPieces(toPieces(imgs));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const close = useCallback(() => setActiveIndex(null), []);
  const next = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i + 1) % pieces.length)),
    [pieces.length],
  );
  const prev = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i - 1 + pieces.length) % pieces.length)),
    [pieces.length],
  );

  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activeIndex, close, next, prev]);

  return (
    <>
      <div className="pt-28 pb-24 px-6">
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: easeManor }}
          className="max-w-3xl mx-auto text-center"
        >
          <p className="u-label">The Salon</p>
          <h1 className="mt-4 font-display text-[clamp(2.8rem,7vw,5rem)] leading-[0.98]">
            A wall of <em>afternoons</em>.
          </h1>
          <div className="mt-5 flex justify-center">
            <span className="u-script" style={{ fontSize: "2.4rem" }}>
              scenes from the manor
            </span>
          </div>
          <div className="mt-8">
            <Divider />
          </div>
        </motion.header>

        {loading && pieces.length === 0 ? (
          <p className="mt-16 text-center u-label">Arranging the wall…</p>
        ) : (
          <div className="max-w-6xl mx-auto mt-16">
            <div
              className="[column-count:1] sm:[column-count:2] lg:[column-count:3]"
              style={{ columnGap: "1.75rem" }}
            >
              {pieces.map((p, i) => (
                <motion.button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setActiveIndex(i);
                    trackEvent("gallery_image_opened", { index: i });
                  }}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.9, ease: easeManor, delay: (i % 3) * 0.08 }}
                  whileHover={{ y: -4, rotate: 0 }}
                  className="group block w-full mb-7 break-inside-avoid text-left"
                  style={{ transform: `rotate(${p.tilt}deg)` }}
                  aria-label={`Open image ${i + 1}`}
                >
                  <div className="frame-gilt transition-transform duration-500">
                    <img
                      src={p.src}
                      alt={`Moyle Manor gallery ${i + 1}`}
                      loading="lazy"
                      className={`w-full ${heightFor(p.span)} object-cover transition-[filter] duration-500 group-hover:brightness-[1.04]`}
                    />
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {activeIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: easeManor }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-6"
            style={{ backgroundColor: "oklch(0.22 0.04 30 / 0.82)", backdropFilter: "blur(6px)" }}
            onClick={close}
            onTouchStart={(e) => setTouchStart(e.targetTouches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchStart === null) return;
              const diff = touchStart - e.changedTouches[0].clientX;
              if (diff > 50) next();
              else if (diff < -50) prev();
              setTouchStart(null);
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                close();
              }}
              aria-label="Close"
              className="absolute top-6 right-6 w-11 h-11 grid place-items-center rounded-full text-[color:var(--parchment)] border border-[color:var(--gilt-hairline)]/60 hover:bg-[color:var(--claret)]/40 transition"
            >
              <FiX size={20} />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Previous image"
              className="absolute left-4 md:left-10 w-12 h-12 grid place-items-center rounded-full text-[color:var(--parchment)] border border-[color:var(--gilt-hairline)]/60 hover:bg-[color:var(--claret)]/40 transition"
            >
              <FiChevronLeft size={22} />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Next image"
              className="absolute right-4 md:right-10 w-12 h-12 grid place-items-center rounded-full text-[color:var(--parchment)] border border-[color:var(--gilt-hairline)]/60 hover:bg-[color:var(--claret)]/40 transition"
            >
              <FiChevronRight size={22} />
            </button>

            <motion.div
              key={pieces[activeIndex].id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.55, ease: easeManor }}
              onClick={(e) => e.stopPropagation()}
              className="frame-gilt max-w-[92vw] max-h-[84vh]"
            >
              <img
                src={pieces[activeIndex].src}
                alt={`Moyle Manor gallery ${activeIndex + 1}`}
                className="max-w-[90vw] max-h-[80vh] object-contain"
              />
            </motion.div>

            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 u-label text-[color:var(--parchment)]/80">
              {activeIndex + 1} &nbsp;/&nbsp; {pieces.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;
